import { orderBy } from "lodash-es";
import { memo, useMemo } from "react";
import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { ProjectOverviewItemDto } from "@/api/types";
import { ProjectLogo } from "@/components/ProjectLogo/ProjectLogo";
import { Badge } from "@/design-system/Badge";
import { cardSurfaceVariants } from "@/design-system/Card";
import { Container } from "@/design-system/Container";
import { Tiles } from "@/design-system/Tiles";
import { isDefined } from "@/helpers/util";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import { RuntimeBackgroundImage } from "@/ui-adapters/RuntimeBackgroundImage";

import { calculateStats } from "../helpers";
import { AddProjectButton } from "./AddProjectButton";
import { BluePill } from "./BluePill";

interface Props {
  items: ProjectOverviewItemDto[];
  showLiveStats: boolean;
  onShowAddProject: () => void;
}

export const TilesView = memo(function TilesView({ items, showLiveStats, onShowAddProject }: Props): React.ReactNode {
  const { t } = useTranslation();

  const sortedItems = useMemo(() => orderBy(items, (x) => x.name.toLocaleLowerCase()), [items]);

  const canSeeAdoptionOrEngagement = items.some(
    (x) => x.userRoleType !== "caretaker" && x.userRoleType !== "maintenance",
  );

  const resolveImage = useImageResolver();

  return (
    <Container fullWidth>
      <Tiles columns={{ base: 1, container: { md: 2, xl: 3 } }} gap="md">
        {sortedItems.map((x) => {
          const stats = calculateStats(x);
          const feelingAtHomeStat = showLiveStats ? stats.feelingAtHomeRating.live : stats.feelingAtHomeRating.periodic;
          const averageTicketStat = showLiveStats ? stats.averageTicketRating.live : stats.averageTicketRating.periodic;
          const adoptionStat = showLiveStats ? stats.adoptionPercentage.live : stats.adoptionPercentage.periodic;
          const engagedResidentStat = showLiveStats
            ? stats.engagedResidentPercentage.live
            : stats.engagedResidentPercentage.periodic;

          return (
            <Tile key={x.id} slug={x.slug} isFaded={x.activeState === "closed"}>
              {(x.type === "companyBased" ||
                x.activeState === "test" ||
                x.activeState === "demo" ||
                x.activeState === "closed") && (
                <aside className="absolute top-space-sm left-space-sm z-raised flex items-center gap-space-xs">
                  {x.activeState === "closed" && (
                    <Badge variant="danger">{t("component.project-item.badge.closed")}</Badge>
                  )}
                  {x.activeState === "test" && (
                    <Badge variant="special">{t("component.project-item.badge.test")}</Badge>
                  )}
                  {x.activeState === "demo" && (
                    <Badge variant="special">{t("component.project-item.badge.demo")}</Badge>
                  )}
                  {x.type === "companyBased" && (
                    <Badge variant="info">{t("component.project-item.badge.company-based")}</Badge>
                  )}
                </aside>
              )}
              <RuntimeBackgroundImage
                imageUrl={x.backgroundImage ? resolveImage(x.backgroundImage, "intrinsic") : undefined}
                presentation="cardHeader"
              >
                <span className="ml-auto size-illustration-compact">
                  <ProjectLogo shape="flushTop" src={x.logoUrl} />
                </span>
              </RuntimeBackgroundImage>
              <article className="flex w-full flex-col px-space-md py-space-lg">
                <span
                  title={
                    x.name + (x.type === "addressBased" ? "" : ` - ${t("component.project-item.badge.company-based")}`)
                  }
                  className="truncate text-body-emphasis"
                >
                  {x.name}
                </span>
                <span className="truncate text-caption-emphasis">{x.city}</span>
                <dl className="mt-space-sm grid grid-cols-2 justify-between gap-y-space-xs text-text-secondary">
                  <dt className="text-caption whitespace-nowrap">{t("page.portfolio.table.header.fah-rating")}</dt>
                  <dd className="flex items-center justify-self-end text-caption-emphasis">
                    {feelingAtHomeStat.value ? feelingAtHomeStat.value : "-"}
                    {isDefined(feelingAtHomeStat.changePercentage) && (
                      <BluePill percentage={feelingAtHomeStat.changePercentage} />
                    )}
                  </dd>
                  <dt className="text-caption whitespace-nowrap">{t("page.portfolio.table.header.avg-rating")}</dt>
                  <dd className="flex items-center justify-self-end text-caption-emphasis">
                    {averageTicketStat.value ? averageTicketStat.value : "-"}
                    {isDefined(averageTicketStat.changePercentage) && (
                      <BluePill percentage={averageTicketStat.changePercentage} />
                    )}
                  </dd>
                  {canSeeAdoptionOrEngagement && (
                    <>
                      <dt className="text-caption whitespace-nowrap">{t("page.portfolio.table.header.adoption")}</dt>
                      <dd className="flex items-center justify-self-end text-caption-emphasis">
                        {adoptionStat.value ? adoptionStat.value + "%" : "-"}
                        {isDefined(adoptionStat.changePercentage) && (
                          <BluePill percentage={adoptionStat.changePercentage} />
                        )}
                      </dd>
                    </>
                  )}
                  {canSeeAdoptionOrEngagement && (
                    <>
                      <dt className="text-caption whitespace-nowrap">{t("page.portfolio.table.header.engagement")}</dt>
                      <dd className="flex items-center justify-self-end text-caption-emphasis">
                        {engagedResidentStat.value ? engagedResidentStat.value + "%" : "-"}
                        {isDefined(engagedResidentStat.changePercentage) && (
                          <BluePill percentage={engagedResidentStat.changePercentage} />
                        )}
                      </dd>
                    </>
                  )}
                </dl>
              </article>
            </Tile>
          );
        })}
        <Tile>
          <header className="flex h-card-media w-full items-center justify-center rounded-t-lg bg-background-brand-light bg-cover px-space-md">
            <svg
              className="h-illustration-compact text-text-secondary"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="0.8"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </header>
          <section className="flex min-h-card-centered-state flex-1 flex-col items-center justify-center p-space-lg">
            <AddProjectButton onClick={onShowAddProject}>{t("page.portfolio.add-new-project")}</AddProjectButton>
          </section>
        </Tile>
      </Tiles>
    </Container>
  );
});

type TileProps = {
  children: React.ReactNode;
  slug?: string;
  isFaded?: boolean;
};

function Tile({ slug, children, isFaded }: TileProps) {
  const Component = slug ? Link : "div";

  return (
    <Component
      data-testid="tile-element"
      to={(slug ? routes.home.home({ slug }) : undefined) as any}
      className={twJoin(
        "group relative flex w-full flex-col overflow-hidden p-px text-left",
        cardSurfaceVariants({ borderTone: "secondary", interactive: Boolean(slug) }),
        slug && "hocus:bg-background-secondary",
        isFaded && "opacity-80",
      )}
    >
      {children}
    </Component>
  );
}
